import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFilterContext } from '@/context';
import usePageMetadata from '@/hooks/usePageMetadata';
import {
  pathRouteStrategy,
  queryRouteStrategy,
  type FilterRouteState,
} from '@/utils/filterRouteStrategy';
import { getCurrentMonthDateRange, getLastTwentyEightDaysDateRange } from '@/utils';
import type { DateRange, PageType } from '@/types';
import { DateString } from '@/types/state/filter';

export interface UseFilterNavigationResult {
  /** Current show/date/network state read from the active URL shape (path params or query string). */
  currentParams: FilterRouteState;
  /** Default date range for the current page (28 days for insights pages, current month otherwise). */
  getDefaultDateRange: () => DateRange;
  /** Navigate to a network: resets the show to "all" and keeps the current date range. */
  goToNetwork: (networkId: string) => void;
  /** Navigate to a show within the active network(s), keeping the current date range. */
  goToShow: (showId: string) => void;
  /** Navigate to a date range within the active network(s), keeping the current show. */
  goToDateRange: (from: DateString, to: DateString) => void;
  /** Replace the URL with a validated show/date range (used by URL-sync redirects). */
  goToValidParams: (showIds: string[], from: DateString, to: DateString) => void;
}

/**
 * Single React-aware entry point for filter/network navigation. It reads the current page type once,
 * selects the matching {@link pathRouteStrategy} or {@link queryRouteStrategy}, and exposes
 * intent-based navigation callbacks plus the current URL state. Callers (filters, pages, URL-sync)
 * never branch on the URL shape or hand-build URLs themselves.
 */
const useFilterNavigation = (): UseFilterNavigationResult => {
  const { page } = usePageMetadata();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { setFilters } = useFilterContext();

  const pageType: PageType = page ?? 'earnings';
  const strategy = pageType === 'content-performance' ? queryRouteStrategy : pathRouteStrategy;

  const getDefaultDateRange = useCallback((): DateRange => {
    const isInsightsPage =
      pathname.includes('audience') || pathname.includes('content-performance');

    return isInsightsPage
      ? getLastTwentyEightDaysDateRange()
      : getCurrentMonthDateRange();
  }, [pathname]);

  const context = useMemo(
    () => ({
      pageType,
      navigate,
      setSearchParams,
      params,
      searchParams,
      defaultDateRange: getDefaultDateRange(),
      setFilters,
    }),
    [pageType, navigate, setSearchParams, params, searchParams, getDefaultDateRange, setFilters]
  );

  const currentParams = useMemo(
    () => strategy.readState({ params, searchParams }),
    [strategy, params, searchParams]
  );

  const applyChange = useCallback(
    (patch: Partial<FilterRouteState>, options?: { replace?: boolean }) => {
      strategy.applyFilterChange(patch, context, options);
    },
    [strategy, context]
  );

  const goToNetwork = useCallback(
    (networkId: string) => applyChange({ networkIds: [networkId], showIds: [] }),
    [applyChange]
  );

  const goToShow = useCallback(
    (showId: string) => applyChange({ showIds: showId === 'all' ? [] : [showId] }),
    [applyChange]
  );

  const goToDateRange = useCallback(
    (from: DateString, to: DateString) => applyChange({ from, to }),
    [applyChange]
  );

  const goToValidParams = useCallback(
    (showIds: string[], from: DateString, to: DateString) =>
      applyChange({ showIds, from, to }, { replace: true }),
    [applyChange]
  );

  return {
    currentParams,
    getDefaultDateRange,
    goToNetwork,
    goToShow,
    goToDateRange,
    goToValidParams,
  };
};

export default useFilterNavigation;
